/**
 * Command Suggestion Menu Component
 *
 * Animasyonlu dropdown/popover menü
 * Input'un üstünde görünür
 * React Portal kullanarak overflow sorunlarını önler
 */

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { CommandItem } from "./CommandItem";
import { CommandLoader } from "./CommandLoader";
import { useCommandSuggestions } from "../hooks/use-command-suggestions";
import { useSlashCommands } from "../hooks/use-slash-commands";
import type { SlashCommand } from "../types/command-types";

interface CommandSuggestionMenuProps {
  inputValue: string;
  isOpen: boolean;
  inputElement?: HTMLElement | null; // Input element reference for positioning
  /** Composer'ın kendi setMessage'i — yoksa global store yazılır, dock/streaming'de parent açılmaz */
  onSelect?: (command: SlashCommand) => void;
}

/** Composer kutusunu tercih et; textarea dar (dock + butonları arası) kalır. */
function resolveAnchor(el: HTMLElement): HTMLElement {
  return el.closest<HTMLElement>("[data-slash-anchor]") ?? el;
}

const VIEWPORT_MARGIN = 8;
const COMPACT_WIDTH = 320;

export const CommandSuggestionMenu: React.FC<CommandSuggestionMenuProps> = ({
  inputValue,
  isOpen,
  inputElement,
  onSelect,
}) => {
  const { t } = useTranslation();
  const { suggestions, isLoading, error, hasResults } =
    useCommandSuggestions(inputValue);
  const { handleCommandSelect: defaultSelect, selectedIndex } =
    useSlashCommands();
  const selectCommand = onSelect ?? defaultSelect;
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLButtonElement>>(new Map());
  const isCompact = position.width > 0 && position.width < COMPACT_WIDTH;

  // Calculate position based on composer (veya input) elementi
  useEffect(() => {
    if (!isOpen || !inputElement) return;

    const updatePosition = () => {
      if (!inputElement) return;

      const anchor = resolveAnchor(inputElement);
      const rect = anchor.getBoundingClientRect();
      const menuElement = menuRef.current;

      // Menu henüz render olmamışsa, tahmini height kullan
      const menuHeight = menuElement?.offsetHeight || 0;

      // Input'un üstüne yerleştir (8px gap ile)
      // Eğer üstte yer yoksa, input'un altına yerleştir
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      const estimatedMenuHeight = menuHeight || 320; // max-h-80 = 320px

      let top: number;
      if (spaceAbove >= estimatedMenuHeight + VIEWPORT_MARGIN) {
        // Üstte yeterli yer var - input'un üstüne
        top = rect.top - estimatedMenuHeight - VIEWPORT_MARGIN;
      } else if (spaceBelow >= estimatedMenuHeight + VIEWPORT_MARGIN) {
        // Altta yeterli yer var - input'un altına
        top = rect.bottom + VIEWPORT_MARGIN;
      } else {
        // Her iki tarafta da yeterli yer yok - üstte göster (scroll edilebilir)
        top = Math.max(
          VIEWPORT_MARGIN,
          rect.top - estimatedMenuHeight - VIEWPORT_MARGIN,
        );
      }

      const maxWidth = window.innerWidth - VIEWPORT_MARGIN * 2;
      const width = Math.min(Math.max(rect.width, 0), maxWidth);
      let left = rect.left;
      if (left + width > window.innerWidth - VIEWPORT_MARGIN) {
        left = window.innerWidth - VIEWPORT_MARGIN - width;
      }
      if (left < VIEWPORT_MARGIN) {
        left = VIEWPORT_MARGIN;
      }

      setPosition({
        top,
        left,
        width,
      });
    };

    // İlk position hesaplama
    updatePosition();

    // Menu render olduktan sonra tekrar hesapla (gerçek height ile)
    const timeoutId = setTimeout(() => {
      updatePosition();
    }, 100);

    // ResizeObserver ile menu height değişikliklerini dinle
    let resizeObserver: ResizeObserver | null = null;
    if (menuRef.current) {
      resizeObserver = new ResizeObserver(() => {
        updatePosition();
      });
      resizeObserver.observe(menuRef.current);
    }

    // Debounce scroll/resize events
    let scrollTimeoutId: NodeJS.Timeout;
    const handleUpdate = () => {
      clearTimeout(scrollTimeoutId);
      scrollTimeoutId = setTimeout(updatePosition, 10);
    };

    window.addEventListener("scroll", handleUpdate, true);
    window.addEventListener("resize", handleUpdate);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(scrollTimeoutId);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener("scroll", handleUpdate, true);
      window.removeEventListener("resize", handleUpdate);
    };
  }, [isOpen, inputElement, hasResults, suggestions.length]);

  // Scroll selected item into view when selectedIndex changes
  useEffect(() => {
    if (!isOpen || selectedIndex < 0 || !hasResults) return;

    const selectedItem = itemRefs.current.get(selectedIndex);
    if (selectedItem) {
      // Smooth scroll to selected item
      selectedItem.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [selectedIndex, isOpen, hasResults]);

  // Set item ref callback
  const setItemRef = (index: number) => (element: HTMLButtonElement | null) => {
    if (element) {
      itemRefs.current.set(index, element);
    } else {
      itemRefs.current.delete(index);
    }
  };

  // Don't render if not open
  if (!isOpen) return null;

  // Don't show anything if just "/" (no search yet)
  const isJustSlash = inputValue === "/" || inputValue === "";

  const menuContent = (
    <AnimatePresence>
      <motion.div
        ref={menuRef}
        initial={{ opacity: 0, y: 10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.95 }}
        transition={{ duration: 0.15, ease: "easeOut" }}
        className="glass-card fixed z-[999] max-h-80 overflow-y-auto rounded-[22px] border-module-accent/20 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.45)] backdrop-blur-2xl dark:border-module-accent/25 [background-color:color-mix(in_srgb,var(--card)_42%,transparent)]"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          width: `${position.width}px`,
        }}
      >
        {/* Suggestions List */}
        {hasResults && !isLoading && (
          <div className="flex flex-col gap-1 p-2">
            {suggestions.map((command, index) => (
              <CommandItem
                key={command.id}
                ref={setItemRef(index)}
                command={command}
                onSelect={() => selectCommand(command)}
                isSelected={index === selectedIndex}
                compact={isCompact}
              />
            ))}
          </div>
        )}

        {isLoading && <CommandLoader />}

        {error && (
          <div className="px-4 py-6 text-center text-sm text-destructive dark:text-red-400">
            {error}
          </div>
        )}

        {!isLoading &&
          !hasResults &&
          !error &&
          !isJustSlash &&
          inputValue.length > 1 && (
            <div className="px-4 py-6 text-center text-sm text-text-tertiary dark:text-gray-400">
              {t("slashCommands.noResults")}
            </div>
          )}
      </motion.div>
    </AnimatePresence>
  );

  // Render using Portal to body to avoid overflow issues
  return typeof window !== "undefined"
    ? createPortal(menuContent, document.body)
    : null;
};
